import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { InviteVisitorForm } from '@/components/InviteVisitorForm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useEmployeeContext } from '@/hooks/useEmployeeContext';
import { cancelInvitation, getInvitations } from '@/services/api.client';
import { formatDateTime, statusBadgeClass } from '@/lib/utils';

export function EmployeeVisitorsPage() {
  const ctx = useEmployeeContext();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (searchParams.get('create') === '1') setOpen(true);
  }, [searchParams]);

  // Include unitId in the query key so employees from different companies on the
  // same property never share a cache entry. The server scopes the response to
  // the employee's unit, so the key must reflect that boundary.
  const invitationsQuery = useQuery({
    queryKey: ['invitations', ctx?.propertyId, ctx?.unitId],
    queryFn: () => (ctx ? getInvitations(ctx.propertyId, { limit: 25 }) : null),
    enabled: !!ctx,
  });

  const data = invitationsQuery.data;
  const isLoading = invitationsQuery.isLoading;

  const cancelMut = useMutation({
    mutationFn: ({ id }: { id: string }) =>
      ctx
        ? cancelInvitation(ctx.propertyId, id)
        : Promise.reject(new Error('Missing employee context')),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invitations', ctx?.propertyId, ctx?.unitId] });
      toast.success('Invitation cancelled');
    },
    onError: () => toast.error('Failed to cancel invitation'),
  });

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl">Visitors</h1>
        <Button onClick={() => setOpen(true)}>
          <Plus className="size-4 mr-2" />
          Invite Visitor
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">All Invitations</CardTitle>
        </CardHeader>
        <CardContent>
          {invitationsQuery.isError ? (
            <div
              role="alert"
              className="rounded-xl border border-destructive/40 bg-destructive/5 p-4"
            >
              <p className="text-sm font-medium">Couldn&apos;t load invitations</p>
              <p className="text-sm text-muted-foreground">Please try again later.</p>
            </div>
          ) : isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : data?.data.length ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Visitor</TableHead>
                  <TableHead>Gate</TableHead>
                  <TableHead>Valid From</TableHead>
                  <TableHead>Valid Until</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.data.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-medium">{inv.visitorName}</TableCell>
                    <TableCell>{inv.gateName}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDateTime(inv.validFrom)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDateTime(inv.validUntil)}
                    </TableCell>
                    <TableCell>
                      <Badge className={statusBadgeClass(inv.status)}>{inv.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {inv.status === 'active' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => cancelMut.mutate({ id: inv.id })}
                          disabled={cancelMut.isPending && cancelMut.variables?.id === inv.id}
                        >
                          Cancel
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="py-10 text-center">
              <Users className="size-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No invitations yet.</p>
              <Button variant="link" size="sm" onClick={() => setOpen(true)} className="mt-1">
                Create your first invitation
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Invite Visitor</DialogTitle>
          </DialogHeader>
          {ctx && (
            <InviteVisitorForm
              propertyId={ctx.propertyId}
              scope="employee"
              onSuccess={() => {
                void qc.invalidateQueries({
                  queryKey: ['invitations', ctx.propertyId, ctx.unitId],
                });
                setOpen(false);
                toast.success('Invitation sent');
              }}
              onCancel={() => setOpen(false)}
              submitLabel="Invite"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
